/*IMPORTS */

/*React & React module dependencies */
import React, { useEffect, useState, useCallback } from 'react';
import { Row, Col } from 'react-bootstrap';

/*Third-party modules */
import { format } from 'date-fns';

/*Shared components */
import { NorwegianDatepicker } from 'components/NorwegianDatepicker';

/*Components */
import BarChart from './components/BarChart';

/*Services */
import { BehandletStatistikk } from 'services/QAApi/types';
import QaApi from 'services/QAApi';

/*Styling */
import Style from './index.module.css';
import CommonLoader from 'components/CommonLoader';

/*IMPORTS END */

/*Interface */
interface Props {
  includeMonthAndYear?: boolean;
}

const QAStatistics: React.FC<Props> = (props) => {
  /*useState properties */
  const [data, setData] = useState<BehandletStatistikk | undefined>();
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [loading, setLoading] = useState<boolean>(false);

  /*functions */
  const loadApiData = useCallback(async () => {
    setLoading(true);
    const response = await QaApi.getBehandletStatistikk(format(startDate, 'dd.MM.yyyy'));
    setData(response);
    setLoading(false);
  }, [startDate]);

  /*useEffect */
  useEffect(() => {
    loadApiData();
  }, [startDate, loadApiData]);

  /*Content */
  if (!data) {
    return (
      <div style={{ marginLeft: '50%' }}>
        <CommonLoader />
      </div>
    ); // Loading animation from Bootstrap!
  }
  return (
    <div className={Style.main}>
      <div className="mt-3 mb-3">
        <NorwegianDatepicker
          value={startDate}
          onChange={(date: Date | undefined) => {
            if (date) setStartDate(date);
          }}
        />
        {loading ? <CommonLoader /> : null}
      </div>
      <Row className={Style.statRow}>
        {data.periods
          .filter((o) => o.group === 'daily')
          .map((period) => {
            return (
              <Col md={12} lg={6} className={Style.statCol} key={period.periodName}>
                <Row>
                  <Col>
                    <div>
                      <h3>{period.periodName}</h3>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col className={Style.statusStats}>
                    <div className={Style.statContainer}>
                      <h2 className={Style.statHeader}>{period.antallAvist}</h2>
                      <h2 className={Style.statNameHeader}>Avviste</h2>
                    </div>
                    <div className={Style.statContainer}>
                      <h2 className={Style.statHeader}>{period.antallBehandlet}</h2>
                      <h2 className={Style.statNameHeader}>Behandlet</h2>
                    </div>
                  </Col>
                </Row>
                <div>
                  <BarChart data={period.details} />
                </div>
              </Col>
            );
          })}
        {props.includeMonthAndYear &&
          data.periods
            .filter((o) => o.group === 'monthly')
            .map((period) => {
              return (
                <Col md={12} lg={6} className={Style.statCol} key={period.periodName}>
                  <Row>
                    <Col>
                      <div>
                        <h3>{period.periodName}</h3>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col className={Style.statusStats}>
                      <div className={Style.statContainer}>
                        <h2 className={Style.statHeader}>{period.antallAvist}</h2>
                        <h2 className={Style.statNameHeader}>Avviste</h2>
                      </div>
                      <div className={Style.statContainer}>
                        <h2 className={Style.statHeader}>{period.antallBehandlet}</h2>
                        <h2 className={Style.statNameHeader}>Behandlet</h2>
                      </div>
                    </Col>
                  </Row>
                  <div>
                    <BarChart data={period.details} />
                  </div>
                </Col>
              );
            })}
        {props.includeMonthAndYear &&
          data.periods
            .filter((o) => o.group === 'yearly')
            .map((period) => {
              return (
                <Col md={12} lg={6} className={Style.statCol} key={period.periodName}>
                  <Row>
                    <Col>
                      <div>
                        <h3>{period.periodName}</h3>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col className={Style.statusStats}>
                      <div className={Style.statContainer}>
                        <h2 className={Style.statHeader}>{period.antallAvist}</h2>
                        <h2 className={Style.statNameHeader}>Avviste</h2>
                      </div>
                      <div className={Style.statContainer}>
                        <h2 className={Style.statHeader}>{period.antallBehandlet}</h2>
                        <h2 className={Style.statNameHeader}>Behandlet</h2>
                      </div>
                    </Col>
                  </Row>
                  <div>
                    <BarChart data={period.details} />
                  </div>
                </Col>
              );
            })}
      </Row>
    </div>
  );
};

export default QAStatistics;
